<script setup lang="ts">
import { computed, ref } from 'vue';

interface Props {
  exchange: string;
  size?: 'sm' | 'md';
}

const props = withDefaults(defineProps<Props>(), {
  size: 'md',
});

const imgError = ref(false);

// Map exchange string to a short abbreviation for fallback text
const EXCHANGE_ABBR: Record<string, string> = {
  FORECASTEX: 'FX',
  KALSHI: 'KL',
};

const abbreviation = computed(() => {
  if (!props.exchange) return '??';
  return EXCHANGE_ABBR[props.exchange.toUpperCase()] ?? props.exchange.slice(0, 2).toUpperCase();
});

// Exchange logo from tws.proxy API — path is always /forecastx, exchange param selects the icon
const logoSrc = computed(() => {
  if (!props.exchange) return '';
  return `/tws.proxy/public/icons/forecastx?exchange=${props.exchange.toLowerCase()}`;
});

const sizeClass = computed(() => {
  return props.size === 'sm' ? 'w-5 h-5 text-[7px]' : 'w-5 h-5 text-[9px]';
});

function onImgError() {
  imgError.value = true;
}
</script>

<template>
  <span
    class="exchange-badge inline-flex items-center justify-center rounded-full overflow-hidden shrink-0 border-1 border-surface-200 dark:border-surface-400"
    :class="sizeClass"
    :title="exchange"
  >
    <img v-if="!imgError" :src="logoSrc" :alt="exchange" class="w-full h-full object-cover" @error="onImgError" />
    <span
      v-else
      class="flex items-center justify-center w-full h-full rounded-full bg-surface-hover font-black text-on-surface"
    >
      {{ abbreviation }}
    </span>
  </span>
</template>
